<script setup>
import AddTenantsToServerAction from "@frontJs/components/actions/projectManagement/dev/AddTenantsToServerAction.vue";
import TenantServerActionsCell
  from "@frontJs/components/tables/cells/projectManagement/dev/TenantServerActionsCell.vue";
import TenantServerDetailsCell
  from "@frontJs/components/tables/cells/projectManagement/dev/TenantServerDetailsCell.vue";
import { ExclamationTriangleIcon, PlusIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";
import StatusCellV2 from "@js/components/tables/cells/StatusCellV2.vue";
import { formatDate } from "@js/utils/formatter";

const { t } = useI18n();

const { route } = useRoute();

const { userSessionKey, server } = defineProps({
  userSessionKey: {
    type: String,
    required: false,
    default: null
  },
  server: {
    type: Object,
    required: true,
    default: null
  }
});

const columnHelper = createColumnHelper();

// TODO: make it a global variabl
const phpVersionMaintainedUntilDates = [
  {
    version: "5.4",
    maintainedUntil: "2015-09-03"
  },
  {
    version: "5.6",
    maintainedUntil: "2018-12-31"
  },
  {
    version: "7.0",
    maintainedUntil: "2019-01-10"
  },
  {
    version: "7.1",
    maintainedUntil: "2019-12-01"
  },
  {
    version: "7.2",
    maintainedUntil: "2020-11-30"
  },
  {
    version: "7.3",
    maintainedUntil: "2021-12-06"
  },
  {
    version: "7.4",
    maintainedUntil: "2022-11-28"
  },
  {
    version: "8.0",
    maintainedUntil: "2023-11-26"
  },
  {
    version: "8.1",
    maintainedUntil: "2025-12-31"
  },
  {
    version: "8.2",
    maintainedUntil: "2026-12-31"
  },
  {
    version: "8.3",
    maintainedUntil: "2027-12-31"
  },
  {
    version: "8.4",
    maintainedUntil: "2028-12-31"
  }
];

const {
  loading,
  table,
  count,
  globalFilter,
  filters,
  availableFilters,
  setGlobalFilter,
  setFilters,
  updateData
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.name,
          href: route("front.projectManagement.customer.updateCustomer", {
            id: props.row.original.id
          }),
          target: "_self",
          inertia: true
        })
    }),
    columnHelper.accessor(() => null, {
      id: "cloudflarePackageId",
      header: () => t("label.cloudflare"),
      cell: (props) =>
        h(StatusCellV2, {
          status: props.row.original.cloudflarePackage || props.row.original.freeCloudflare || props.row.original.proCloudflare
        })
    }),
    columnHelper.accessor(() => null, {
      id: "phpVersion",
      header: () => t("label.phpVersion"),
      cell: (props) =>
        props.row.original.phpVersion
          ? h(TextCellV2, {
            text: props.row.original.phpVersion
          })
          : h(CrossIconCell)
    }),
    columnHelper.accessor(() => null, {
      id: "phpVersion",
      header: () => t("label.phpVersionMaintainedUntil"),
      cell: (props) =>
        props.row.original.phpVersion
          ? h(TextCellV2, {
            text: formatDate(
              phpVersionMaintainedUntilDates.find(
                (item) => item.version === props.row.original.phpVersion
              ).maintainedUntil
            ),
            tooltipContent:
              new Date(
                phpVersionMaintainedUntilDates.find(
                  (item) => item.version === props.row.original.phpVersion
                ).maintainedUntil
              ) < new Date()
                ? t("text.phpVersionNotMaintained", {
                  version: props.row.original.phpVersion
                })
                : null,
            tooltipIcon: ExclamationTriangleIcon,
            tooltipPlacement: "top",
            tooltipColor: "red"
          })
          : h(CrossIconCell)
    }),
    columnHelper.accessor(() => null, {
      id: "otherServers",
      header: () => t("label.otherServers"),
      cell: (props) =>
        props.row.original.otherServers &&
        props.row.original.otherServers.length > 0
          ? h(TextCellV2, {
            text: props.row.original.otherServers
              .map((server) => server.name)
              .join(", ")
          })
          : h(CrossIconCell),
      enableSorting: false
    }),
    columnHelper.display({
      id: "details",
      header: t("label.details"),
      cell: (innerProps) =>
        h(TenantServerDetailsCell, {
          row: innerProps.row
        })
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(TenantServerActionsCell, {
          row: innerProps.row,
          server: server,
          callback: updateData
        })
    })
  ],
  url: route("front.projectManagement.dev.postTenantsServer"),
  data: {
    serverId: server.id
  },
  columnPinning: {
    right: ['actions']
  },
  userSessionKey: userSessionKey
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :filters="filters"
      :available-filters="availableFilters"
      :set-filters="setFilters"
      :set-global-filter="setGlobalFilter"
    >
      <template #actions>
        <AddTenantsToServerAction :server="server" :callback="updateData">
          <template #button="{ setIsOpen }">
            <ActionButton
              size="small"
              :callback="() => setIsOpen(true)"
              :left-icon="PlusIcon"
            >
              {{ t("button.addCustomer") }}
            </ActionButton>
          </template>
        </AddTenantsToServerAction>
      </template>
    </DataTableLayout>
  </div>
</template>
